import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';

/**
 * <yebo-book-now> — Floating sticky "Book Now" button that opens the
 * existing <yebo-booking> widget inside a native <dialog> modal.
 *
 * Single-script embed: the same bundle registers both <yebo-booking> (inline)
 * and <yebo-book-now> (popup). Restaurants pick whichever tag fits each page.
 *
 * Usage:
 *   <script src="yebo-booking.js" defer></script>
 *   <yebo-book-now
 *     location-id="loc_xxx"
 *     calendar-id="cal_xxx"
 *     api-base="/api/widget"
 *     phone-number="+27 11 123 4567"
 *   ></yebo-book-now>
 */
@customElement('yebo-book-now')
export class YeboBookNow extends LitElement {
  static styles = [
    css`
      :host {
        display: contents;
      }

      /* ── Sticky wrapper (positions both attribution + button) ──────── */

      .fab-wrapper {
        position: fixed;
        bottom: calc(24px + env(safe-area-inset-bottom, 0px));
        right: 24px;
        z-index: 2147483646;
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 6px;
      }

      /* HOVER-REVEAL attribution (design round 4, 2026-08-05 — Simon's idea):
         the corner shows ONLY the button at rest; hovering it darkens the
         glass AND fades "Powered by YeboGuest" in above, DinePlan-placed but
         transient. Progressive disclosure: branding appears exactly when the
         guest shows intent, never as a permanent sticker on the host page.
         Hover-capable pointers only — on touch it simply never shows (the
         modal footer carries attribution there). aria-hidden: decorative. */

      .fab-attribution {
        font-family: system-ui, -apple-system, sans-serif;
        font-size: 10.5px;
        font-weight: 400;
        letter-spacing: 0.02em;
        color: rgba(255, 255, 255, 0.85);
        text-shadow: 0 1px 6px rgba(0, 0, 0, 0.55), 0 0 2px rgba(0, 0, 0, 0.4);
        text-align: right;
        white-space: nowrap;
        pointer-events: none;
        user-select: none;
        opacity: 0;
        transform: translateY(5px);
        transition: opacity 0.18s ease, transform 0.18s ease;
      }

      .fab-attribution-brand {
        color: #fff;
        font-weight: 600;
      }

      @media (hover: hover) {
        .fab-wrapper:hover .fab-attribution,
        .fab-wrapper:focus-within .fab-attribution {
          opacity: 1;
          transform: translateY(0);
        }
      }

      /* ── Sticky button — SMOKED GLASS ─────────────────────────────────
         Chosen over white-frost deliberately: this button ships onto
         arbitrary third-party sites, and a dark translucent material is the
         one treatment that keeps contrast on light pages, dark pages and
         photography alike. Corners at 10px (squarer per design review).
         --yebo-fab-accent (set from theme-primary) tints border + icon
         for venue branding without giving up the background-proof smoke. */

      .fab {
        display: inline-flex;
        align-items: center;
        gap: 10px;
        padding: 14px 26px;
        border-radius: 10px;
        font-family: system-ui, -apple-system, sans-serif;
        font-size: 15px;
        font-weight: 570;
        letter-spacing: 0.025em;
        cursor: pointer;
        min-height: 48px;
        color: #fff;
        background: rgba(17, 19, 23, 0.55);
        backdrop-filter: blur(16px) saturate(1.4);
        -webkit-backdrop-filter: blur(16px) saturate(1.4);
        border: 1px solid var(--yebo-fab-accent, rgba(255, 255, 255, 0.28));
        box-shadow:
          0 10px 30px rgba(0, 0, 0, 0.3),
          inset 0 1px 0 rgba(255, 255, 255, 0.22);
        transition: background 0.15s ease, transform 0.15s ease;
      }

      .fab:hover {
        background: rgba(22, 25, 30, 0.68);
        transform: translateY(-1px);
      }

      .fab:active {
        transform: translateY(0);
        background: rgba(14, 16, 20, 0.72);
      }

      /* No backdrop-filter (a sliver of browsers): solid near-black keeps
         the same legibility, just without the glass. */
      @supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
        .fab { background: rgba(17, 19, 23, 0.85); }
      }

      .fab-icon {
        width: 17px;
        height: 17px;
        flex-shrink: 0;
        color: var(--yebo-fab-accent-icon, #fff);
      }

      /* Mobile: the SAME compact pill, tucked bottom-right — not a full-width
         banner. The old full-width treatment dominated small screens and sat
         across page content (flagged on the first real phone look, 2026-08-05);
         a corner pill keeps the page readable and still lands in thumb reach. */
      @media (max-width: 540px) {
        .fab-wrapper {
          right: 16px;
          /* 16 → 32px base lift (design review 2026-08-05): 16px sat cramped
             against Safari's bottom toolbar; the extra breathing room reads
             less boxed-in without leaving thumb reach. Safe-area still adds
             on top for home-indicator devices. */
          bottom: calc(32px + env(safe-area-inset-bottom, 0px));
        }
        .fab {
          padding: 13px 22px;
          font-size: 15px;
        }
      }

      /* ── Dialog modal ────────────────────────────────────────────── */

      dialog {
        padding: 0;
        border: none;
        background: transparent;
        max-width: 720px;
        width: calc(100% - 32px);
        max-height: calc(100dvh - 48px);
        border-radius: 12px;
        overflow: visible;
        color: inherit;
      }

      dialog::backdrop {
        background: rgba(10, 10, 14, 0.6);
        backdrop-filter: blur(4px);
      }

      .modal-frame {
        background: #1a1a1e;
        border-radius: 12px;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.45);
        display: flex;
        flex-direction: column;
        max-height: calc(100dvh - 48px);
        overflow: hidden;
      }

      .modal-close {
        position: absolute;
        top: 10px;
        right: 10px;
        width: 36px;
        height: 36px;
        border-radius: 50%;
        border: none;
        background: rgba(255, 255, 255, 0.08);
        color: #e8dcc8;
        cursor: pointer;
        font-size: 20px;
        line-height: 1;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        z-index: 3;
        transition: background 150ms ease;
      }
      .modal-close:hover {
        background: rgba(255, 255, 255, 0.16);
      }
      .modal-close:focus-visible {
        outline: 2px solid #c4a87a;
        outline-offset: 2px;
      }

      /* ── Mobile: the modal becomes a full-height SHEET ──────────────────
         (design 2026-08-05, from the Creation phone screenshots): no floating
         card with awkward gaps — the booking slides up and IS the screen.
         The page disappears behind it; content scrolls between the progress
         header (sidebar-nav's mobile row) and the step's sticky action bar. */
      @media (max-width: 540px) {
        dialog {
          width: 100vw;
          max-width: 100vw;
          height: 100dvh;
          max-height: 100dvh;
          margin: 0;
          border-radius: 0;
        }
        .modal-frame {
          height: 100dvh;
          max-height: 100dvh;
          border-radius: 0;
          padding-bottom: env(safe-area-inset-bottom, 0px);
        }
        .modal-close {
          top: 12px;
          right: 12px;
          width: 40px;
          height: 40px;
          background: rgba(255, 255, 255, 0.14);
        }
      }

      .modal-body {
        flex: 1;
        overflow-y: auto;
        overscroll-behavior: contain;
        -webkit-overflow-scrolling: touch;
      }

      .modal-body yebo-booking {
        display: block;
        margin: 0;
        max-width: none;
        box-shadow: none;
        border-radius: 0;
      }

      /* ── Powered-by footer ───────────────────────────────────────── */

      .powered-by {
        padding: 9px 16px;
        text-align: center;
        font-family: system-ui, -apple-system, sans-serif;
        font-size: 11px;
        color: rgba(232, 220, 200, 0.5);
        background: #141418;
        border-top: 1px solid rgba(255, 255, 255, 0.06);
      }
      .powered-by-brand {
        color: rgba(232, 220, 200, 0.85);
        font-weight: 600;
      }
    `,
  ];

  // ── Public attributes (mostly pass-through to <yebo-booking>) ────────────

  @property({ type: String, attribute: 'location-id' })
  locationId = '';

  @property({ type: String, attribute: 'calendar-id' })
  calendarId = '';

  @property({ type: String, attribute: 'api-base' })
  apiBase = '';

  @property({ type: String, attribute: 'theme-primary' })
  themePrimary = '';

  @property({ type: String })
  timezone = '';

  @property({ type: String, attribute: 'phone-number' })
  phoneNumber = '';

  // "Book a Table" over "Book Now" (design round 1): says restaurant, not
  // app. Venues override via the `label` attribute.
  @property({ type: String })
  label = 'Book a Table';

  @property({ type: Boolean, attribute: 'hide-powered-by' })
  hidePoweredBy = false;

  /**
   * `hide-button` — render NO floating button; the host page supplies its own
   * trigger and calls `open()`. This is the "your button, our modal" embed
   * (first asked for by Creation Wines, whose page already has a styled
   * "Reserve a table" button — and whose corner is occupied by a competing
   * provider's FAB):
   *
   *   <yebo-book-now hide-button location-id="loc_xxx" …></yebo-book-now>
   *   <script>
   *     document.querySelector('#reserve-btn').addEventListener('click', (e) => {
   *       e.preventDefault();
   *       document.querySelector('yebo-book-now').open();
   *     });
   *   </script>
   */
  @property({ type: Boolean, attribute: 'hide-button' })
  hideButton = false;

  // ── Internal state ───────────────────────────────────────────────────────

  @state() private _open = false;

  @query('dialog') private _dialog!: HTMLDialogElement;
  @query('.fab') private _fab!: HTMLButtonElement;

  // ── Lifecycle ────────────────────────────────────────────────────────────

  private _onDialogClose = () => {
    this._open = false;
    this._fab?.focus();
  };

  // ── Public API ───────────────────────────────────────────────────────────

  /**
   * Open the booking modal programmatically — the host page's own button can
   * be the trigger (see `hide-button` above). Safe to call before first
   * render completes (no-ops until the dialog exists, same guard the FAB
   * path always had).
   */
  open() {
    this._handleOpen();
  }

  /** Close the booking modal programmatically. */
  close() {
    this._handleClose();
  }

  // ── Event handlers ───────────────────────────────────────────────────────

  private _handleOpen() {
    if (!this._dialog) return;
    this._dialog.showModal();
    this._open = true;
  }

  private _handleClose() {
    if (!this._dialog) return;
    this._dialog.close();
  }

  // ── Render ───────────────────────────────────────────────────────────────

  render() {
    return html`
      ${this.hideButton ? nothing : html`
      <div class="fab-wrapper">
        ${this.hidePoweredBy ? nothing : html`
          <span class="fab-attribution" aria-hidden="true">
            Powered by <span class="fab-attribution-brand">YeboGuest</span>
          </span>
        `}
        <button
          type="button"
          class="fab"
          style=${this.themePrimary
            ? `--yebo-fab-accent: ${this.themePrimary}; --yebo-fab-accent-icon: ${this.themePrimary};`
            : ''}
          aria-label=${this.label === 'Book a Table' ? 'Book a reservation' : this.label}
          aria-haspopup="dialog"
          aria-expanded=${this._open ? 'true' : 'false'}
          @click=${this._handleOpen}
        >
          <svg class="fab-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM9 10H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2z"/>
          </svg>
          <span>${this.label}</span>
        </button>
      </div>
      `}

      <dialog
        aria-label="Book a reservation"
        @close=${this._onDialogClose}
      >
        <div class="modal-frame">
          <button
            type="button"
            class="modal-close"
            aria-label="Close booking"
            @click=${this._handleClose}
          >&times;</button>

          <div class="modal-body">
            <yebo-booking
              location-id=${this.locationId}
              calendar-id=${this.calendarId}
              api-base=${this.apiBase}
              theme-primary=${this.themePrimary}
              timezone=${this.timezone}
              phone-number=${this.phoneNumber}
            ><!-- #848: this FAB is the OUTERMOST shadow host on a floating-button
                 embed, so the payment step hangs its light-DOM Stripe mount node on
                 <yebo-book-now>. Forward it down into <yebo-booking>, which forwards
                 it into the payment step — every shadow host in the chain must. -->
              <slot name="stripe-payment" slot="stripe-payment"></slot>
            </yebo-booking>
          </div>

          ${this.hidePoweredBy
            ? null
            : html`
                <div class="powered-by">
                  Powered by <span class="powered-by-brand">YeboGuest</span>
                </div>
              `}
        </div>
      </dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'yebo-book-now': YeboBookNow;
  }
}
